import { cn } from '@sistemas/ui'
import { Monitor, Moon, Sun } from 'lucide-react'
import { useTheme } from 'next-themes'
import type * as React from 'react'

type ThemeName = 'light' | 'dark' | 'system'

const ITEMS: readonly { id: ThemeName; icon: React.ReactNode; label: string }[] = [
  { id: 'light', icon: <Sun className="h-3.5 w-3.5" />, label: 'Claro' },
  { id: 'dark', icon: <Moon className="h-3.5 w-3.5" />, label: 'Escuro' },
  { id: 'system', icon: <Monitor className="h-3.5 w-3.5" />, label: 'Sistema' },
]

/**
 * Seletor de tema segmentado — os três modos sempre visíveis, o ativo marcado
 * como em qualquer outro controle segmentado do Hub (`bg-primary/15` +
 * `text-primary`).
 *
 * Copiado de PMC/apps/web/src/components/ThemeToggle.tsx. É o padrão do
 * ecossistema: mostra o estado atual e permite ir direto ao modo desejado,
 * em vez de exigir cliques em ciclo.
 *
 * O que NÃO veio junto foi o contraste invertido (`bg-foreground` +
 * `text-background`): no escuro ele acendia um quadrado branco no cabeçalho —
 * o elemento mais claro da tela para dizer "está escuro" — e no claro fazia o
 * oposto. O ativo agora acompanha o tema em vez de brigar com ele, e usa o
 * mesmo par de tokens de Mês/Semana na Agenda e dos filtros de Rotinas, então
 * "selecionado" tem uma aparência só no sistema inteiro.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { theme, setTheme } = useTheme()

  return (
    <div
      className={cn(
        'inline-flex items-center rounded-md border bg-background p-0.5',
        className,
      )}
      role="radiogroup"
      aria-label="Tema"
    >
      {ITEMS.map((item) => {
        const active = theme === item.id
        return (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={item.label}
            onClick={() => setTheme(item.id)}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-primary/15 text-primary'
                : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground',
            )}
            title={item.label}
          >
            {item.icon}
          </button>
        )
      })}
    </div>
  )
}
